import { useEffect, type RefObject } from 'react';

export function wheelAxisDelta(event: Pick<WheelEvent, 'deltaX' | 'deltaY' | 'deltaMode'>, clientSize: number, axis: 'x' | 'y'): number {
  const raw = axis === 'x' ? event.deltaX : event.deltaY;
  if (event.deltaMode === 1) return raw * 16;
  if (event.deltaMode === 2) return raw * clientSize;
  return raw;
}

export function applyAxisScroll(
  node: {
    scrollTop: number;
    scrollLeft: number;
    scrollHeight: number;
    scrollWidth: number;
    clientHeight: number;
    clientWidth: number;
  },
  axis: 'x' | 'y',
  delta: number,
): boolean {
  if (!delta) return false;
  if (axis === 'y') {
    const maxTop = Math.max(0, node.scrollHeight - node.clientHeight);
    const nextTop = Math.max(0, Math.min(maxTop, node.scrollTop + delta));
    if (nextTop === node.scrollTop) return false;
    node.scrollTop = nextTop;
    return true;
  }
  const maxLeft = Math.max(0, node.scrollWidth - node.clientWidth);
  const nextLeft = Math.max(0, Math.min(maxLeft, node.scrollLeft + delta));
  if (nextLeft === node.scrollLeft) return false;
  node.scrollLeft = nextLeft;
  return true;
}

/**
 * Chrome/Safari swallow wheel over input/select, so overflow:auto lists do not
 * scroll when the pointer is on a table cell. Capture the wheel and scroll the
 * element (then an ancestor) ourselves. X and Y are applied separately so a
 * mostly-vertical trackpad swipe is not eaten by horizontal overflow.
 */
export function useOverflowWheelScroll(
  ref: RefObject<HTMLElement | null>,
  parentSelector: string | null,
  enabled = true,
): void {
  useEffect(() => {
    if (!enabled) return;
    const el = ref.current;
    if (!el) return;

    const onWheel = (event: WheelEvent) => {
      if (event.ctrlKey || event.metaKey) return;
      const dy = wheelAxisDelta(event, el.clientHeight, 'y');
      const dx = wheelAxisDelta(event, el.clientWidth, 'x');
      if (!dx && !dy) return;
      const parent = parentSelector ? el.closest<HTMLElement>(parentSelector) : null;
      const movedY = applyAxisScroll(el, 'y', dy) || !!(parent && applyAxisScroll(parent, 'y', dy));
      const movedX = applyAxisScroll(el, 'x', dx) || !!(parent && applyAxisScroll(parent, 'x', dx));
      if (movedY || movedX) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    el.addEventListener('wheel', onWheel, { passive: false, capture: true });
    return () => el.removeEventListener('wheel', onWheel, { capture: true });
  }, [ref, parentSelector, enabled]);
}
